import { useEffect, useState } from "react";
import { ChevronRight, Loader2, PenLine, ShieldCheck, Wallet } from "lucide-react";
import { toast } from "sonner";
import { ExportButton } from "@/components/ExportButton";
import { api, apiError, fmtCr } from "@/lib/api";

const ICON = { "Unit Safety Head": ShieldCheck, "Finance Department": Wallet };
const SLUG = (who) => who.toLowerCase().replace(/[^a-z]+/g, "-");

/** One use case waiting on a signature. Clicking it opens the form at the vetting entry. */
const row = (r, who, onFix) => (
  <button
    key={r.id}
    type="button"
    data-testid={`sig-row-${r.code}`}
    onClick={() => onFix(r)}
    className="group grid w-full grid-cols-[1fr_auto] items-start gap-3 border-t border-border px-4 py-3 text-left transition-colors duration-200 hover:bg-muted/60"
  >
    <span>
      <span className="font-data block text-[10px] uppercase tracking-wider text-muted-foreground">
        {r.code} · {r.status}
        {r.phase ? ` · ${r.phase}` : ""}
      </span>
      <span className="block text-sm font-medium leading-snug">{r.title}</span>
      <span className="mt-0.5 block text-[10px] text-muted-foreground">
        {r.ready ? (
          <span className="text-[hsl(var(--healthy))]" data-testid={`sig-ready-${r.code}`}>
            Can be signed today — it is completed
          </span>
        ) : (
          `committed to finish ${r.committed_on || "— no date"}`
        )}
        {r.owner_name ? ` · ${r.owner_name}` : ""}
      </span>
    </span>
    <span className="flex items-center gap-2 pt-1">
      <span className="font-data text-sm font-bold tabular-nums">{fmtCr(r.expected_cr)}</span>
      <PenLine
        className="h-3.5 w-3.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
        title={`${who} must sign this`}
      />
    </span>
  </button>
);

/**
 * Whose signature this year's benefit is waiting on. Safety & Surveillance work is certified by
 * the unit's own Safety Head; everything else is Finance's. Nothing here writes anything — a row
 * opens the use case at the vetting entry, which is where the figure and its date are recorded.
 */
export const SignatureQueue = ({ query, onFix, testId = "signature-queue" }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");

  useEffect(() => {
    api
      .get("/current-fy/signatures", { params: query })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(query)]);

  if (!data)
    return (
      <div className="flex h-28 items-center justify-center border border-border bg-card">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );

  const t = data.totals;

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-caps flex items-center gap-2 text-[hsl(var(--watch))]">
            <PenLine className="h-3.5 w-3.5" /> Whose signature it is waiting on
          </div>
          <p className="mt-1 max-w-3xl text-[11px] text-muted-foreground">
            <b className="font-data" data-testid="sig-total-count">
              {t.count}
            </b>{" "}
            of this year's {t.of} usecases carry a benefit figure nobody has signed off —{" "}
            <b className="font-data" data-testid="sig-total-cr">
              {fmtCr(t.cr)}
            </b>{" "}
            standing unvetted, of which{" "}
            <b className="font-data" data-testid="sig-total-ready">
              {t.ready}
            </b>{" "}
            are already completed and can be signed today. {t.signed} usecase(s) ·{" "}
            {fmtCr(t.signed_cr)} are signed already.
          </p>
        </div>
        <ExportButton
          path="/current-fy/signatures/export"
          params={query}
          label="Download the queue"
          testId="export-signatures"
        />
      </div>

      <div className="mt-3 grid gap-px border border-border bg-border lg:grid-cols-2">
        {data.signers.map((g) => {
          const Icon = ICON[g.signer] || PenLine;
          return (
            <div key={g.signer} className="bg-card" data-testid={`sig-group-${SLUG(g.signer)}`}>
              <div className="border-b border-border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider">
                      <Icon className="h-4 w-4 text-primary" /> {g.signer}
                    </div>
                    <p className="mt-1 max-w-sm text-[11px] text-muted-foreground">{g.note}</p>
                  </div>
                  <div className="text-right">
                    <div
                      className="font-data text-2xl font-black tabular-nums"
                      data-testid={`sig-count-${SLUG(g.signer)}`}
                    >
                      {g.count}
                    </div>
                    <div className="font-data text-[11px] text-muted-foreground">
                      {fmtCr(g.cr)} unvetted
                    </div>
                  </div>
                </div>
                <p className="mt-2 font-display text-[10px] font-bold uppercase tracking-widest">
                  {g.ready ? (
                    <span className="text-[hsl(var(--healthy))]">
                      {g.ready} completed · {fmtCr(g.ready_cr)} can be signed today
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      none of these is completed yet — nothing can be signed today
                    </span>
                  )}
                </p>
              </div>

              {!g.units.length && (
                <p className="p-6 text-center text-[11px] text-muted-foreground">
                  Nothing is waiting on {g.signer} in this view.
                </p>
              )}

              {g.units.map((u) => {
                const key = `${g.signer}|${u.plant}`;
                const on = open === key;
                return (
                  <div key={u.plant} className="border-b border-border last:border-0">
                    <button
                      type="button"
                      data-testid={`sig-unit-${SLUG(g.signer)}-${u.plant}`}
                      onClick={() => setOpen(on ? "" : key)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors duration-200 hover:bg-muted"
                    >
                      <span className="flex items-center gap-2">
                        <ChevronRight
                          className={`h-3.5 w-3.5 transition-transform duration-200 ${
                            on ? "rotate-90" : ""
                          }`}
                        />
                        <span className="font-display text-xs font-bold uppercase tracking-widest">
                          {u.plant}
                        </span>
                        <span className="text-[11px] text-muted-foreground">{u.name}</span>
                      </span>
                      <span className="font-data text-right text-[11px] tabular-nums">
                        <b className="text-sm">{u.count}</b> usecase(s) · {fmtCr(u.cr)}
                        {u.ready ? (
                          <span className="block text-[10px] text-[hsl(var(--healthy))]">
                            {u.ready} ready to sign
                          </span>
                        ) : null}
                      </span>
                    </button>
                    {on && <div>{u.rows.map((r) => row(r, g.signer, onFix))}</div>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};
